<template>
  <div :id="id" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true" style="z-index: 1065;">
    <div class="modal-dialog modal-dialog-centered" role="document" style="max-width: 440px;">
      <div class="modal-content text-left position-relative" style="background: #111; border: 1px solid rgba(255,255,255,0.12); border-radius: 24px; box-shadow: 0 20px 50px rgba(0,0,0,0.7); overflow: hidden;">
        
        <!-- Top-Right Close Button -->
        <button type="button" class="close position-absolute" data-dismiss="modal" aria-label="Close" @click="hide"
                style="top: 16px; right: 20px; z-index: 10; opacity: 1; text-shadow: none; background: rgba(255,255,255,0.1); width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: none; cursor: pointer; transition: all 0.2s;">
          <i class="fa fa-times text-white font-size-14"></i>
        </button>

        <div class="modal-body p-4 p-md-5">
          
          <!-- Header Badge & Title -->
          <div class="mb-4 text-center">
            <div class="d-inline-flex align-items-center justify-content-center mb-3" style="width: 56px; height: 56px; border-radius: 50%; background: linear-gradient(135deg, rgba(243,218,107,0.2) 0%, rgba(229,59,185,0.2) 100%); border: 1px solid rgba(243,218,107,0.35);">
              <i class="fa fa-credit-card font-size-24" style="background: linear-gradient(90deg, #F3DA6B, #E53BB9); -webkit-background-clip: text; -webkit-text-fill-color: transparent;"></i>
            </div>
            <h4 class="text-white font-weight-700 mb-1 font-size-20">Add New Card</h4>
            <p class="text-muted font-size-13 mb-0">Secure 256-bit encrypted checkout with Stripe</p>
          </div>

          <!-- Card Number -->
          <div class="form-group mb-3">
            <label class="text-muted font-size-12 text-uppercase font-weight-bold" style="letter-spacing: 0.8px;">Card Number <span class="text-danger">*</span></label>
            <div class="position-relative">
              <i class="fa fa-credit-card position-absolute text-muted" style="left: 14px; top: 15px; font-size: 16px;"></i>
              <input type="text" class="cp-input form-control" 
                     :value="card.number"
                     v-imask="cardMasks" 
                     @accept="onAcceptCardType" 
                     placeholder="0000 0000 0000 0000"
                     style="padding-left: 42px !important; font-family: monospace; font-size: 15px; letter-spacing: 1.5px;">
            </div>
            <div v-if="validationFailed && !card.number" class="text-danger font-size-12 mt-1">Card number is required</div>
          </div>

          <!-- Expiry and CVC Row -->
          <div class="row mb-3">
            <div class="col-7 pr-1">
              <label class="text-muted font-size-12 text-uppercase font-weight-bold" style="letter-spacing: 0.8px;">Expiry Date <span class="text-danger">*</span></label>
              <div class="d-flex align-items-center" style="gap: 6px;">
                <div class="position-relative flex-fill">
                  <select class="cp-input form-control" v-model="card.expMonth" style="appearance: none; padding: 0 10px !important; text-align: center;">
                    <option value="" disabled selected class="bg-dark text-white">MM</option>
                    <option v-for="m in 12" :key="m" :value="m < 10 ? '0'+m : m.toString()" class="bg-dark text-white">
                      {{ m < 10 ? '0'+m : m }}
                    </option>
                  </select>
                </div>
                <span class="text-muted" style="font-size: 18px;">/</span>
                <div class="position-relative flex-fill">
                  <select class="cp-input form-control" v-model="card.expYear" style="appearance: none; padding: 0 10px !important; text-align: center;">
                    <option value="" disabled selected class="bg-dark text-white">YY</option>
                    <option v-for="y in 15" :key="y" :value="(currentYear + y - 1).toString()" class="bg-dark text-white">
                      {{ (currentYear + y - 1).toString().slice(2) }}
                    </option>
                  </select>
                </div>
              </div>
              <div v-if="validationFailed && (!card.expMonth || !card.expYear)" class="text-danger font-size-12 mt-1">Expiry date is required</div>
            </div>
            <div class="col-5 pl-1">
              <label class="text-muted font-size-12 text-uppercase font-weight-bold" style="letter-spacing: 0.8px;">CVC / CVV <span class="text-danger">*</span></label>
              <div class="position-relative">
                <i class="fa fa-lock position-absolute text-muted" style="left: 12px; top: 15px; font-size: 15px;"></i>
                <input type="text" class="cp-input form-control" 
                       :value="card.cvv"
                       v-imask="cvvMask" 
                       @accept="onAcceptCvv" 
                       placeholder="123"
                       style="padding-left: 32px !important; font-family: monospace; font-size: 15px; letter-spacing: 2px;">
              </div>
              <div v-if="validationFailed && !card.cvv" class="text-danger font-size-12 mt-1">CVC required</div>
            </div>
          </div>
          
          <!-- Dual Action Buttons (Side by Side) -->
          <div class="mt-4 d-flex align-items-center justify-content-between" style="gap: 12px;">
            <button type="button" class="cp-btn-ghost flex-fill py-2" @click="hide" data-dismiss="modal">
              Cancel
            </button>
            <button type="button" @click="save" class="cp-btn-primary flex-fill py-2" :disabled="isLoading">
              <i v-if="isLoading" class="fa fa-spinner fa-spin me-1"></i>
              <i v-else class="fa fa-check me-1"></i>
              Save Card
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {IMaskDirective} from 'vue-imask';
import {cardMasks, cvvMask} from "@/mixin/masks";

export default {
  name: "ModelAddCard",
  props: {
    id: {
      type: String,
      default: 'add_card_modal'
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  directives: {
    imask: IMaskDirective
  },
  data() {
    return {
      card: {
        number: '',
        expMonth: '',
        expYear: '',
        cvv: ''
      },
      currentYear: new Date().getFullYear(),
      cardMasks,
      cvvMask,
      validationFailed: false
    }
  },
  methods: {
    show() {
      this.resetForm();
      $(`#${this.id}`).modal('show');
    },
    hide() {
      $(`#${this.id}`).modal('hide');
    },
    resetForm() {
      this.card = {
        number: '',
        expMonth: '',
        expYear: '',
        cvv: ''
      };
      this.validationFailed = false;
    },
    onAcceptCardType(e) {
      const maskRef = e.detail;
      this.$set(this.card, 'number', maskRef.value);
    },
    onAcceptCvv(e) {
      const maskRef = e.detail;
      this.$set(this.card, 'cvv', maskRef.value);
    },
    save() {
      if (!this.card.number || !this.card.expMonth || !this.card.expYear || !this.card.cvv) {
        this.validationFailed = true;
        return;
      }
      
      let payload = {
        ...this.card,
        number: this.card.number.replace(/\s/g, ''),
        expYear: this.card.expYear.length === 2 ? '20' + this.card.expYear : this.card.expYear
      };
      
      this.$emit('save', payload);
    }
  }
}
</script>

<style scoped>
.cp-input {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 12px !important;
  height: 46px !important;
  color: #fff !important;
  font-size: 14px;
}
.cp-input:focus {
  border-color: #E53BB9 !important;
  box-shadow: 0 0 14px rgba(229, 59, 185, 0.35) !important;
}
.close:hover {
  background: rgba(255, 255, 255, 0.2) !important;
}
</style>
